import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Leaf, Sun } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const PT_LINK = "https://youfit.planway.com/";
const BT_LINK = "https://youfit.planway.com/";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();

  return (
    <div className="bg-[var(--bg)]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1671485515432-f00ca46fa579?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHx3YXJtJTIwbGluZW4lMjBjdXJ0YWluJTIwc3VubGlnaHQlMjB3aW5kb3d8ZW58MHx8fHwxNzc3MTA0MjQzfDA&ixlib=rb-4.1.0&q=85"
            alt="Warm linen curtain with soft sunlight"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg)] via-[rgba(251,249,246,0.85)] to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <p className="overline mb-6" data-testid="hero-overline">{t("landing.hero.overline")}</p>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-[color:var(--ink)] leading-[1.05] tracking-tight" data-testid="hero-title">
              {t("landing.hero.title.a")} <em className="text-[color:var(--accent)] not-italic font-normal">{t("landing.hero.title.b")}</em>
              <br />
              {t("landing.hero.title.c")}
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[color:var(--ink-2)] leading-relaxed" data-testid="hero-subtitle">
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={PT_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="hero-cta-pt">
                {t("landing.hero.cta.pt")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </a>
              <a href={BT_LINK} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="hero-cta-bt">
                {t("landing.hero.cta.bt")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </a>
            </div>
            {!user && (
              <p className="mt-6 text-sm text-[color:var(--ink-3)]">
                {t("landing.hero.existing")}{" "}
                <Link to="/login" className="underline underline-offset-4 text-[color:var(--ink-2)] hover:text-[color:var(--accent)]" data-testid="hero-client-login">
                  {t("landing.hero.enter_space")}
                </Link>
              </p>
            )}
          </div>

          <div className="md:col-span-5 fade-up order-first md:order-none">
            <div className="relative max-w-[200px] sm:max-w-[240px] md:max-w-[340px] lg:max-w-[400px] mx-auto md:mx-0 md:ml-auto">
              <div className="absolute -inset-3 md:-inset-4 rounded-[1.4rem] md:rounded-[2rem] bg-[var(--bg-2)] -z-10" />
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/p7tnqipm_IMG_6448%202.jpg"
                alt="Sopfie lifting in the gym"
                className="rounded-[1.2rem] md:rounded-[1.6rem] aspect-[4/5] object-cover object-center w-full shadow-[0_30px_60px_-30px_rgba(42,39,36,0.35)]"
                data-testid="hero-portrait"
              />
              <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-6 bg-[var(--surface)] border border-[color:var(--line-soft)] rounded-2xl px-3 py-2 md:px-5 md:py-4 max-w-[150px] md:max-w-[220px]">
                <p className="font-serif italic text-xs md:text-base text-[color:var(--ink)] leading-tight">
                  {t("landing.hero.coming_home")}
                </p>
                <p className="mt-1 md:mt-2 text-[9px] md:text-xs text-[color:var(--ink-3)] tracking-widest uppercase">— Sopfie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY STRIP */}
      <section className="border-y border-[color:var(--line-soft)] bg-[var(--bg-2)]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-3 gap-8">
          {[
            { icon: Leaf, title: t("pillar.inside_out.title"), body: t("pillar.inside_out.body") },
            { icon: Sun, title: t("pillar.no_quick_fix.title"), body: t("pillar.no_quick_fix.body") },
            { icon: Heart, title: t("pillar.home.title"), body: t("pillar.home.body") },
          ].map((p) => (
            <div key={p.title} className="flex gap-4 items-start">
              <p.icon size={22} strokeWidth={1.3} className="text-[color:var(--accent)] mt-1 shrink-0" />
              <div>
                <p className="font-serif text-xl text-[color:var(--ink)]">{p.title}</p>
                <p className="text-sm text-[color:var(--ink-2)] leading-relaxed mt-1">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MY JOURNEY */}
      <section id="about" className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="overline mb-5">{t("journey.label")}</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] leading-tight tracking-tight">
              {t("journey.title.a")}
              <br />
              <em className="text-[color:var(--accent)] not-italic">{t("journey.title.b")}</em>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[color:var(--ink-2)] text-lg leading-relaxed">
            <p>{t("journey.p1")}</p>
            <p>{t("journey.p2")}</p>
            <p>{t("journey.p3")}</p>
            <p className="font-serif italic text-2xl text-[color:var(--ink)] pt-3">
              {t("journey.tag")}
            </p>
          </div>
        </div>
      </section>

      {/* PAUSES — quiet quotes to drop into the body */}
      <section className="py-10 md:py-14" data-testid="section-pauses" style={{ backgroundColor: "#F3EBE1" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="overline mb-4 text-[color:var(--accent)]">{t("pauses.label")}</p>
          <div className="space-y-5 md:space-y-6">
            {[
              { text: t("pauses.q1"), attr: t("pauses.q1.attr") },
              { text: t("pauses.q2"), attr: t("pauses.q2.attr") },
              { text: t("pauses.q3"), attr: t("pauses.q3.attr") },
            ].map((q, i) => (
              <figure
                key={i}
                className={`max-w-xl ${i % 2 === 0 ? "mr-auto text-left" : "ml-auto text-right"}`}
                data-testid={`pause-${i}`}
              >
                <blockquote className="font-serif italic font-light text-base sm:text-lg text-[color:var(--ink)] leading-snug">
                  {q.text}
                </blockquote>
                <figcaption className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]">
                  {q.attr}
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3 text-[color:var(--ink-3)]">
            <span className="h-px flex-1 bg-[color:var(--line-soft)]" />
            <span className="text-[10px] uppercase tracking-[0.25em]">{t("pauses.divider")}</span>
            <span className="h-px flex-1 bg-[color:var(--line-soft)]" />
          </div>
        </div>
      </section>

      {/* SERVICES (PT + BT) */}
      <section className="py-24 bg-[var(--bg-2)]/60 border-y border-[color:var(--line-soft)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="overline mb-3">{t("services.overline")}</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">
                {t("services.title.a")} <em className="text-[color:var(--accent)] not-italic">{t("services.title.b")}</em>
              </h2>
            </div>
            <p className="text-[color:var(--ink-2)] max-w-md">
              {t("services.lead")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <article className="card-soft overflow-hidden" data-testid="home-service-pt-card">
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/ulx6tgbp_415782729_392355013236514_2514110994634288011_n%202.jpg"
                alt="Sopfie strength training — barbell squat"
                className="w-full aspect-[5/3] object-cover object-[50%_30%]"
              />
              <div className="p-8">
                <p className="overline">{t("services.pt.tag")}</p>
                <h3 className="mt-3 font-serif text-3xl text-[color:var(--ink)]">{t("services.pt.title")}</h3>
                <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">{t("services.pt.summary")}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={PT_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="home-service-pt-cta">
                    {t("common.book_first")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </a>
                  <Link to="/services" className="btn-ghost" data-testid="home-service-pt-learn">
                    {t("common.learn_more")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </Link>
                </div>
              </div>
            </article>

            <article className="card-soft overflow-hidden md:mt-12" data-testid="home-service-bt-card">
              <img
                src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/mf7ok6u8_IMG_9233.jpg"
                alt="Sopfie body therapy session"
                className="w-full aspect-[5/3] object-cover object-[80%_15%]"
              />
              <div className="p-8">
                <p className="overline">{t("services.bt.tag")}</p>
                <h3 className="mt-3 font-serif text-3xl text-[color:var(--ink)]">{t("services.bt.title")}</h3>
                <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">{t("services.bt.summary")}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={BT_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="home-service-bt-cta">
                    {t("common.book_first")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </a>
                  <Link to="/services" className="btn-ghost" data-testid="home-service-bt-learn">
                    {t("common.learn_more")} <ArrowUpRight size={16} strokeWidth={1.6} />
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* SUBTLE EXPLORE STRIP */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid sm:grid-cols-3 gap-3">
            <Link to="/services" className="card-soft p-6 group" data-testid="explore-services">
              <p className="overline">{t("explore.what_we_do")}</p>
              <p className="mt-2 font-serif text-2xl text-[color:var(--ink)] group-hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-2">
                {t("explore.services")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </p>
            </Link>
            <Link to="/testimonials" className="card-soft p-6 group" data-testid="explore-testimonials">
              <p className="overline">{t("explore.words_clients")}</p>
              <p className="mt-2 font-serif text-2xl text-[color:var(--ink)] group-hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-2">
                {t("explore.testimonials")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </p>
            </Link>
            <Link to="/contact" className="card-soft p-6 group" data-testid="explore-contact">
              <p className="overline">{t("explore.get_in_touch")}</p>
              <p className="mt-2 font-serif text-2xl text-[color:var(--ink)] group-hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-2">
                {t("explore.contact")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
